// ============================================================================
// PatientVisitsTab — «Записи и приёмы» кабинета пациента.
//
//   • приёмы ядра «ГидраМедик» (medic_encounters) — список/период из карточки
//     пациента (PatientCardAppointmentsSection, тот же рендер, что у врача);
//   • запись к специалисту (psy_bookings) — ближайшие и прошедшие записи.
//
// Всё ТОЛЬКО ДЛЯ ЧТЕНИЯ: форма приёма/записи пациенту не открывается, запись
// в документы не выполняется. Нет прав — явное ограничение (SSoT сервера).
// ============================================================================

import { useMemo, useState } from 'react';
import { CalendarClock, CalendarDays, Loader2, Stethoscope } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { PatientCardAppointmentsSection } from '@/components/medic/PatientCardAppointmentsSection';
import { SectionState } from '@/components/medic/PatientCardStates';
import { emptyRange, type DateRange } from '@/components/medic/patientCardData';
import { statusToneClass } from '@/lib/cabinet/appointments';
import type { CabinetBooking, CabinetBookings, CabinetEncounters } from '@/lib/patientCabinetDashboard';
import { formatValue } from '@/lib/format';

interface Props {
    encounters: CabinetEncounters;
    bookings: CabinetBookings;
    onRefresh: () => void;
}

function formatDateTime(value: string): string {
    if (!value) return 'Дата не указана';
    return formatValue(value, 'date:dd.MM.yyyy HH:mm', 'timestamp') || value;
}

function bookingDateKey(value: string): string {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : new Date(parsed).toISOString();
}

function isUpcoming(value: string): boolean {
    const parsed = Date.parse(value);
    return !Number.isNaN(parsed) && parsed >= Date.now();
}

/** Карточка записи к специалисту (psy_bookings) — только просмотр. */
function BookingRow({ booking }: { booking: CabinetBooking }) {
    return (
        <div className="flex items-start gap-3 border-b px-3 py-2.5 last:border-0">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{formatDateTime(booking.startAt)}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusToneClass(booking.statusCode)}`}>
                        {booking.statusLabel}
                    </span>
                    {booking.durationMin !== null && (
                        <span className="text-[10px] text-muted-foreground">{booking.durationMin} мин</span>
                    )}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Stethoscope className="h-3 w-3" />
                    <span>{booking.specialistName || 'Специалист не указан'}</span>
                    {booking.serviceName && <span>· {booking.serviceName}</span>}
                </div>
                {booking.comment && (
                    <div className="mt-1 whitespace-pre-wrap break-words text-xs">{booking.comment}</div>
                )}
            </div>
            <span className={`shrink-0 self-center text-[10px] ${isUpcoming(booking.startAt) ? 'text-primary' : 'text-muted-foreground'}`}>
                {isUpcoming(booking.startAt) ? 'предстоит' : 'прошла'}
            </span>
        </div>
    );
}

function BookingsSection({ data, onRefresh }: { data: CabinetBookings; onRefresh: () => void }) {
    const [showPast, setShowPast] = useState(false);
    const { upcoming, past } = useMemo(() => {
        const sorted = [...data.items].sort((a, b) => {
            const aKey = bookingDateKey(a.startAt);
            const bKey = bookingDateKey(b.startAt);
            const aUp = isUpcoming(a.startAt);
            const bUp = isUpcoming(b.startAt);
            if (aUp && bUp) return aKey.localeCompare(bKey);
            if (aUp) return -1;
            if (bUp) return 1;
            return bKey.localeCompare(aKey);
        });
        return {
            upcoming: sorted.filter((item) => isUpcoming(item.startAt)),
            past: sorted.filter((item) => !isUpcoming(item.startAt)),
        };
    }, [data.items]);

    if (data.state === 'denied') {
        return <SectionState title="Записи к специалисту недоступны" text={data.note || data.error} tone="warning" />;
    }
    if (data.state === 'unavailable') {
        return <SectionState title="Записи к специалисту недоступны" text={data.note || data.error} />;
    }
    if (data.state === 'error') {
        return (
            <SectionState
                title="Не удалось загрузить записи к специалисту"
                text={data.error}
                tone="danger"
                onRetry={onRefresh}
            />
        );
    }

    return (
        <div className="overflow-hidden rounded-lg border bg-card">
            <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                <CalendarDays className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium">Запись к специалисту</span>
                <span className="ml-auto text-[10px] text-muted-foreground">
                    предстоит: {upcoming.length} · прошло: {past.length}
                </span>
            </div>
            {data.items.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">Записей к специалисту пока нет.</div>
            ) : (
                <>
                    {upcoming.map((booking) => <BookingRow key={booking.id} booking={booking} />)}
                    {upcoming.length === 0 && past.length > 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Предстоящих записей нет.</div>
                    )}
                    {past.length > 0 && (
                        <div className="border-t">
                            <button
                                type="button"
                                className="w-full px-3 py-1.5 text-left text-[11px] text-muted-foreground hover:text-foreground"
                                onClick={() => setShowPast((value) => !value)}
                            >
                                {showPast ? '▾' : '▸'} Прошедшие записи ({past.length})
                            </button>
                            {showPast && past.map((booking) => <BookingRow key={booking.id} booking={booking} />)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/** Приём — только просмотр (без формы редактирования). */
function EncounterDialog({ id, data, onClose }: {
    id: string;
    data: CabinetEncounters;
    onClose: () => void;
}) {
    const encounter = data.items.find((item) => item.id === id) || null;
    const statusLabel = encounter
        ? data.statusLabels[encounter.statusCode] || encounter.statusCode || '—'
        : '';
    const typeLabel = encounter
        ? data.typeLabels[encounter.typeCode] || encounter.typeCode || ''
        : '';
    return (
        <UniversalWindow
            open={!!id}
            onClose={onClose}
            title={encounter?.number ? `Приём № ${encounter.number}` : 'Приём'}
            width="w-[min(720px,94vw)]"
            height="h-[min(82vh,92vh)]"
            resizable={false}
        >
            <div className="h-full overflow-auto p-4">
                {!encounter ? (
                    <div className="text-sm text-muted-foreground">Приём не найден.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="rounded-lg border bg-card px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Дата и время</div>
                            <div className="text-sm font-medium">{formatDateTime(encounter.appointmentAt)}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusToneClass(encounter.statusCode)}`}>
                                    {statusLabel}
                                </span>
                                {typeLabel && <span className="text-[11px] text-muted-foreground">{typeLabel}</span>}
                            </div>
                        </div>
                        <div className="rounded-lg border bg-card px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Врач</div>
                            <div className="text-sm">{encounter.doctorName || 'Не указан'}</div>
                        </div>
                        <div className="rounded-lg border bg-card px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Жалобы</div>
                            <div className="whitespace-pre-wrap break-words text-sm">{encounter.complaints || '—'}</div>
                        </div>
                        <div className="rounded-lg border bg-card px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Заключение</div>
                            <div className="whitespace-pre-wrap break-words text-sm">{encounter.conclusion || '—'}</div>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Приём доступен только для просмотра. Изменения вносит медицинский специалист.
                        </div>
                    </div>
                )}
            </div>
        </UniversalWindow>
    );
}

export function PatientVisitsTab({ encounters, bookings, onRefresh }: Props) {
    const [range, setRange] = useState<DateRange>(emptyRange);
    const [appointmentId, setAppointmentId] = useState('');

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                <Stethoscope className="h-3.5 w-3.5 text-primary" />
                <span>Только просмотр — приёмы и записи изменяет медицинский специалист.</span>
            </div>
            {encounters.state === 'ready' ? (
                <PatientCardAppointmentsSection
                    data={encounters}
                    range={range}
                    onRangeChange={setRange}
                    onReload={onRefresh}
                    onOpenAppointment={setAppointmentId}
                />
            ) : (
                <SectionState
                    title={
                        encounters.state === 'denied'
                            ? 'Приёмы недоступны'
                            : encounters.state === 'error'
                                ? 'Не удалось загрузить приёмы'
                                : 'Приёмы не найдены'
                    }
                    text={encounters.error || encounters.note}
                    tone={encounters.state === 'error' ? 'danger' : encounters.state === 'denied' ? 'warning' : 'muted'}
                    onRetry={onRefresh}
                />
            )}

            <BookingsSection data={bookings} onRefresh={onRefresh} />

            {appointmentId && (
                <EncounterDialog id={appointmentId} data={encounters} onClose={() => setAppointmentId('')} />
            )}
        </div>
    );
}

/** Индикатор загрузки раздела (для состояний «ещё не загружено»). */
export function VisitsLoading() {
    return (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка записей…
        </div>
    );
}
